import { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

export interface ChartDataPoint {
  date: Date;
  total: number;
  [key: string]: Date | number;
}

interface ChartDataModalProps {
  open: boolean;
  onClose: () => void;
  dataPoints: ChartDataPoint[];
  onChange: (dataPoints: ChartDataPoint[]) => void;
}

const ChartDataModal = ({
  open,
  onClose,
  dataPoints,
  onChange,
}: ChartDataModalProps) => {
  const [localDataPoints, setLocalDataPoints] =
    useState<ChartDataPoint[]>(dataPoints);
  const [newDate, setNewDate] = useState<string>("");
  const [newTotal, setNewTotal] = useState<string>("");
  const [dateError, setDateError] = useState<string>("");
  const [totalError, setTotalError] = useState<string>("");

  const validateInputs = useCallback((): boolean => {
    let isValid = true;

    if (!newDate) {
      setDateError("Date is required");
      isValid = false;
    } else {
      setDateError("");
    }

    if (!newTotal) {
      setTotalError("Total value is required");
      isValid = false;
    } else if (isNaN(parseFloat(newTotal))) {
      setTotalError("Please enter a valid number");
      isValid = false;
    } else {
      setTotalError("");
    }

    return isValid;
  }, [newDate, newTotal]);

  const handleAdd = useCallback(() => {
    if (!validateInputs()) {
      return;
    }

    const newDataPoint: ChartDataPoint = {
      date: new Date(newDate),
      total: parseFloat(newTotal),
    };

    const updatedDataPoints = [...localDataPoints, newDataPoint].sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );

    setLocalDataPoints(updatedDataPoints);
    setNewDate("");
    setNewTotal("");
    setDateError("");
    setTotalError("");
  }, [newDate, newTotal, localDataPoints, validateInputs]);

  const handleRemove = useCallback(
    (index: number) => {
      const updatedDataPoints = localDataPoints.filter((_, i) => i !== index);
      setLocalDataPoints(updatedDataPoints);
    },
    [localDataPoints]
  );

  const handleSave = useCallback(() => {
    onChange(localDataPoints);
    onClose();
  }, [localDataPoints, onChange, onClose]);

  const handleCancel = useCallback(() => {
    setLocalDataPoints(dataPoints);
    setNewDate("");
    setNewTotal("");
    setDateError("");
    setTotalError("");
    onClose();
  }, [dataPoints, onClose]);

  const handleClearAll = useCallback(() => {
    setLocalDataPoints([]);
  }, []);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        handleAdd();
      }
    },
    [handleAdd]
  );

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="chart-data-modal-title"
    >
      <DialogTitle id="chart-data-modal-title">Manage Chart Data</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "#666" }}>
            Add New Data Point
          </Typography>
          <Box display="flex" alignItems="flex-start" gap={2}>
            <TextField
              label="Date"
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              error={!!dateError}
              helperText={dateError}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
              inputProps={{
                "aria-label": "Select date for data point",
              }}
            />
            <TextField
              label="Total Value ($)"
              type="number"
              value={newTotal}
              onChange={(e) => setNewTotal(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              error={!!totalError}
              helperText={totalError}
              sx={{ flex: 1 }}
              inputProps={{
                "aria-label": "Enter total value for data point",
                min: 0,
              }}
            />
            <IconButton
              color="primary"
              onClick={handleAdd}
              aria-label="Add data point"
              sx={{ mt: 0.5 }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1, color: "#666" }}>
          Current Data Points ({localDataPoints.length})
        </Typography>

        {localDataPoints.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "#999", textAlign: "center", py: 3 }}
          >
            No data points added yet. Use the form above to add data.
          </Typography>
        ) : (
          <List
            sx={{
              maxHeight: 300,
              overflow: "auto",
              border: "1px solid #E0E0E0",
              borderRadius: 1,
            }}
          >
            {localDataPoints.map((point, index) => (
              <ListItem
                key={`${point.date.toISOString()}-${index}`}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={`Remove data point from ${point.date.toLocaleDateString()}`}
                    onClick={() => handleRemove(index)}
                    tabIndex={0}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
                sx={{
                  "&:hover": { backgroundColor: "#f5f5f5" },
                  borderBottom: "1px solid #E0E0E0",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <ListItemText
                  primary={point.date.toLocaleDateString()}
                  secondary={formatCurrency(point.total)}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClearAll}
          variant="outlined"
          color="error"
          disabled={localDataPoints.length === 0}
          aria-label="Clear all data points"
        >
          Clear All
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleCancel} aria-label="Cancel changes">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          aria-label="Save data points"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChartDataModal;
